import type { Course } from '@/payload-types'
import { CourseCard } from '@/components/course/CourseCard'
import { BookOpen, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

type CoursesGridBlockProps = {
  blockType: 'coursesGrid'
  id?: string | null
  label?: string | null
  heading?: string | null
  description?: string | null
  showAllButton?: boolean | null
  courses?: (Course | number)[] | null
}

export const CoursesGridBlockComponent: React.FC<CoursesGridBlockProps> = ({
  label,
  heading,
  description,
  showAllButton,
  courses,
}) => {
  // Filter to only populated courses (not just IDs)
  const docs = (courses || []).filter((c): c is Course => typeof c === 'object' && c !== null)

  return (
    <section className="pt-16 pb-8 md:pt-24 md:pb-12" aria-label="Cursusoverzicht" id="cursussen">
      <div className="container">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="space-y-3">
            {label && (
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                {label}
              </p>
            )}
            {heading && (
              <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                {heading}
              </h2>
            )}
            {description && <p className="max-w-lg text-muted-foreground">{description}</p>}
          </div>
          {showAllButton && (
            <Button asChild variant="outline" className="rounded-full">
              <Link href="/cursussen" className="inline-flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Bekijk alle cursussen
              </Link>
            </Button>
          )}
        </div>

        {docs.length ? (
          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {docs.map((course) => (
              <CourseCard course={course} key={course.id} />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border-2 border-dashed border-border/60 bg-gradient-to-br from-card to-muted/20 p-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
              <BookOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">Nog geen cursussen</h3>
            <p className="text-muted-foreground">
              Er zijn nog geen cursussen gepubliceerd. Voeg een cursus toe in de beheeromgeving om
              deze hier te tonen.
            </p>
          </div>
        )}

        <div className="mt-12 text-center">
          <a
            href="https://www.voedingsportenleefstijl.nl/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#085e41] px-7 py-3.5 font-semibold text-white shadow-lg shadow-[#085e41]/25 transition-all hover:gap-3 hover:bg-[#2D5A3D] hover:shadow-xl hover:shadow-[#085e41]/30"
          >
            Persoonlijke coaching (1-op-1)
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </section>
  )
}
